// =============================================================================
// Shared formatting utilities — Single source of truth
// =============================================================================

/** Format number as full USD string ($1,234.56) using Intl */
export function formatCurrency(value: number, options?: { maxDecimals?: number; minDecimals?: number }): string {
  const maxDecimals = options?.maxDecimals ?? 2;
  const minDecimals = options?.minDecimals ?? 2;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: minDecimals,
    maximumFractionDigits: maxDecimals,
  }).format(value);
}

/**
 * Format a P&L value with sub-cent precision when needed.
 *
 * Use this for realized / unrealized / total P&L displays where rounding
 * a tiny non-zero value (e.g. $0.003) to "$0.00" would visually claim
 * break-even when the user actually made (or lost) a fraction of a cent.
 *
 * Rules:
 *   - Exactly 0 → "$0.00"
 *   - |v| < 0.01 (non-zero) → 4 decimals so the real outcome is visible:
 *     "$0.0034" (gain) / "-$0.0091" (loss). Avoids the misleading
 *     "+$0.00 green" that javi14 hit on closed weather markets.
 *   - |v| >= 0.01 → standard 2 decimals: "$2.34", "-$15.00".
 *
 * Sign prefix is NOT added here — callers control "+"/"-" presentation
 * (often coupled to the color class) so this stays a pure formatter.
 */
export function formatPnL(value: number): string {
  if (!Number.isFinite(value) || value === 0) return "$0.00";
  const abs = Math.abs(value);
  const decimals = abs < 0.01 ? 4 : 2;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

/** Format number as compact USD string ($1.2M, $45K, $123) */
export function formatCompactCurrency(value: number | null | undefined): string {
  const num = typeof value === "number" ? value : Number(value) || 0;
  if (num === 0) return "$0";
  const abs = Math.abs(num);
  const sign = num < 0 ? "-" : "";
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(1)}K`;
  if (abs < 10) return `${sign}$${abs.toFixed(2)}`;
  return `${sign}$${abs.toFixed(0)}`;
}
